/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// src/utils/conversions.ts

/**
 * Download conversion reporting (N-2).
 *
 * A download is the conversion this site exists to produce, and until now
 * nothing reported one. This module is the single place that does.
 *
 * Two events are sent for one download, because they answer different
 * questions:
 *
 *  - `file_download` to Google Analytics — which product, from which page, how
 *    often. This is the reporting event.
 *  - `conversion` to Google Ads — the optimisation signal that bidding uses.
 *    Sent only when a conversion label is configured, because Google Ads
 *    silently discards a conversion whose `send_to` names no real conversion
 *    action, and a silently discarded signal is worse than an absent one.
 *
 * ── Why the queue ────────────────────────────────────────────────────────────
 *
 * The Ads tag is injected asynchronously by `index.html`, which means a visitor
 * who lands and immediately clicks Download can beat it. `window.gtag` does not
 * exist yet at that moment, so the call would be lost — and that visitor, the
 * fastest converter on the page, is precisely the one worth counting.
 *
 * Calls made before the tag is ready are held here and replayed by
 * `installConversionReplayHook`, which `index.html` invokes through
 * `window.__ds_replay_conversions` once the script finishes loading.
 *
 * Once `window.gtag` exists we hand off directly and stop queueing: from that
 * point Google's own `dataLayer` buffers anything the script has not processed,
 * so a second queue on top of it would only duplicate events.
 *
 * ── On consent ───────────────────────────────────────────────────────────────
 *
 * There is deliberately no consent check here. The tag runs in advanced consent
 * mode with all four signals defaulting to denied, so Google itself decides
 * what an event may carry: with advertising consent it is an observed
 * conversion; without it, a modelled one with no identifier attached. Gating
 * these calls on our side would discard the modelled conversions too, which is
 * the entire reason advanced consent mode was chosen (N-1).
 */

import {
  findProductSlugInPath,
  isKnownProductSlug,
} from "@/data/productCatalog";
import { resolveConversionLabel } from "@/utils/adsConversionLabels";

/** One deferred `window.gtag(...)` invocation. */
type GtagCall = readonly [string, ...unknown[]];

export interface DownloadConversionInput {
  /** Display name of the product, used only as an event label. */
  productName?: string;
  /** The URL the download was served from. */
  downloadUrl?: string;
  /**
   * Product slug, when the caller knows it.
   *
   * The home page knows it (each card carries one) but is not served from a
   * product path. Product pages do not pass it and do not need to — the path
   * they are served from names the product. Anything unrecognised is ignored
   * rather than trusted.
   */
  slug?: string;
}

/**
 * Upper bound on deferred calls.
 *
 * Reaching it means the Ads script never loaded — blocked, offline, or simply
 * not configured. In that case the queue can never drain, so it must not grow
 * without limit for the life of the page. Twenty is far above any real session:
 * it takes ten downloads before the tag arrives to fill it.
 */
const MAX_QUEUED_CALLS = 20;

/**
 * Placeholder left in the built file when the variable is unset. Kept in step
 * with the guard in index.html — an unsubstituted `%VITE_...%` must never be
 * treated as configuration.
 */
const ADS_ID_PLACEHOLDER = "AW-XXXXXXXXX";

let pendingCalls: GtagCall[] = [];
let replayHookInstalled = false;

/**
 * Whether an environment variable holds a real value.
 *
 * Rejects the empty string, the unsubstituted `%VITE_...%` placeholder, and the
 * documented example value — the three ways this can be "set" while meaning
 * nothing.
 */
function isConfigured(
  value: string | undefined,
  placeholder: string,
): value is string {
  return (
    typeof value === "string" &&
    value !== "" &&
    value.charAt(0) !== "%" &&
    value !== placeholder
  );
}

/**
 * The Google Ads `send_to` target, or undefined when either half is missing.
 *
 * Both halves are required: the account id identifies the advertiser, the label
 * identifies the specific conversion action within it. An account id alone
 * addresses nothing.
 */
function resolveAdsSendTo(slug: string | undefined): string | undefined {
  const adsId = import.meta.env.VITE_GA_ADS_ID as string | undefined;
  const fallbackLabel = import.meta.env.VITE_GA_ADS_DOWNLOAD_LABEL as
    | string
    | undefined;

  // V-3e: the product's own conversion destination, falling back to the shared
  // one. Some signal is better than none — a download still counts, it just
  // cannot be told apart until the per-product value is set.
  const label = resolveConversionLabel(slug, fallbackLabel);

  // Unchanged and deliberate: send nothing when the account identifier is
  // missing. Google Ads discards a conversion aimed at an action that does not
  // exist, and a discarded signal is worse than an absent one, because it looks
  // like it worked.
  if (!isConfigured(adsId, ADS_ID_PLACEHOLDER)) return undefined;
  if (!isConfigured(label, "XXXXXXXXXXXXXXXXXXX")) return undefined;

  return `${adsId}/${label}`;
}

/**
 * Send a gtag call now, or hold it until the tag is ready.
 *
 * The try/catch is deliberate and narrow. `window.gtag` is third-party code we
 * do not control; if a tag error escaped from here it would surface as an
 * unhandled exception in the click handler of a download button. Measurement
 * failing must never look, to the visitor, like the download failing.
 */
function sendOrQueue(call: GtagCall): void {
  if (typeof window.gtag === "function") {
    try {
      window.gtag(...(call as [string, ...unknown[]]));
    } catch {
      // Reported by the tag's own error handling; nothing useful to add, and
      // nothing here should interrupt the download.
    }
    return;
  }

  if (pendingCalls.length < MAX_QUEUED_CALLS) {
    pendingCalls.push(call);
  }
}

/**
 * Last segment of a URL path, e.g. `ScreenRecorder.exe`.
 *
 * Google Analytics reports downloads by file name, so this is what makes the
 * `file_download` event legible in their standard report. Falls back to the
 * whole value when the URL cannot be parsed rather than dropping the event —
 * an odd file name is still better than a missing conversion.
 */
function extractFileName(downloadUrl: string): string {
  const withoutQuery = downloadUrl.split(/[?#]/)[0];
  const segments = withoutQuery.split("/");
  return segments[segments.length - 1] || withoutQuery;
}

/**
 * Report that a visitor downloaded a product.
 *
 * Safe to call from a click handler: it never throws and never blocks. The
 * caller does not need to know whether any tag is loaded, configured, or
 * consented to.
 */
export function trackDownloadConversion(input: DownloadConversionInput): void {
  const { productName = "", downloadUrl = "", slug } = input;

  // An explicit slug wins, but only if it names a real product. Otherwise fall
  // back to the path — on a product page that is authoritative.
  const resolvedSlug = isKnownProductSlug(slug)
    ? slug
    : findProductSlugInPath(window.location.pathname);

  sendOrQueue([
    "event",
    "file_download",
    {
      // GA4's own recommended parameter names for this event. Using them is
      // what puts the data in the built-in report instead of a custom one.
      file_name: downloadUrl ? extractFileName(downloadUrl) : "",
      link_url: downloadUrl,
      // Our dimensions. `product_slug` is the stable key; `product_name` is
      // human-readable and translated, so it is a label, never a key.
      product_slug: resolvedSlug ?? "unknown",
      product_name: productName,
    },
  ]);

  const sendTo = resolveAdsSendTo(resolvedSlug);
  if (sendTo) {
    sendOrQueue([
      "event",
      "conversion",
      {
        send_to: sendTo,
        // Google Ads deduplicates on this within a conversion window. Without
        // it, a visitor who clicks Download twice — which the modal invites,
        // since it offers a retry link — is counted twice.
        transaction_id: `dl-${resolvedSlug ?? "unknown"}-${Date.now()}`,
      },
    ]);
  }
}

/**
 * Replay everything deferred while the tag was loading.
 *
 * The queue is cleared before the first call is made. If a replayed call were
 * to re-enter this function, an uncleared queue would replay itself forever.
 */
export function flushConversionQueue(): void {
  if (pendingCalls.length === 0) return;

  const calls = pendingCalls;
  pendingCalls = [];

  for (const call of calls) {
    sendOrQueue(call);
  }
}

/**
 * Expose the replay function to `index.html`, which calls it from the Ads
 * script's onload handler.
 *
 * Called once from `main.tsx`. Flushes immediately as well, because the tag may
 * already have finished loading before React mounted — in which case its onload
 * has fired and will not fire again.
 */
export function installConversionReplayHook(): void {
  if (replayHookInstalled) return;
  replayHookInstalled = true;
  window.__ds_replay_conversions = flushConversionQueue;
  flushConversionQueue();
}

/**
 * Clear module state between tests.
 *
 * The queue and the installed-hook flag live at module scope and survive
 * `vi.clearAllMocks()`, so without this one test's deferred calls would replay
 * inside the next one.
 */
export function resetConversionsForTests(): void {
  pendingCalls = [];
  replayHookInstalled = false;
}
